import React, { useRef, useEffect, useMemo, useState } from 'react';

export default function EarlyClosures({ fields, parentClass }) {


    const alertTitle =
        typeof fields?.alert_title === "string"
            ? fields.alert_title.trim()
            : "Early Closures";

    const alertTiming = Array.isArray(fields?.alert_timing)
        ? fields.alert_timing.filter(
            (item) =>
                item &&
                typeof item.date === "string" &&
                typeof item.text === "string" &&
                typeof item.timings === "string"
        )
        : [];

    const hasValidData = alertTiming.length > 0;

    // Screen size detection hook
    const [screenSize, setScreenSize] = useState(() => {
        if (typeof window !== 'undefined') {
            const width = window.innerWidth;
            if (width >= 1024) return 'desktop';
            if (width >= 768) return 'tablet';
            return 'mobile';
        }
        return 'desktop';
    });


    const parsedTimings = useMemo(() => {
        if (!hasValidData) return [];

        return alertTiming.map((item) => {
            let month = "";
            let day = "";

            try {
                // API provides date-only values like "2026-03-23".
                // Never parse these with local timezone, otherwise it can shift by +/- 1 day.
                const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item.date);
                if (match) {
                    const mm = parseInt(match[2], 10); // 1-12
                    const dd = parseInt(match[3], 10); // 1-31

                    const MONTHS = [
                        "JAN",
                        "FEB",
                        "MAR",
                        "APR",
                        "MAY",
                        "JUN",
                        "JUL",
                        "AUG",
                        "SEP",
                        "OCT",
                        "NOV",
                        "DEC",
                    ];

                    month = MONTHS[mm - 1] || "";
                    day = Number.isFinite(dd) ? String(dd) : "";
                } else {
                    // Fallback for unexpected formats
                    const dateObj = new Date(item.date);
                    if (!isNaN(dateObj.getTime())) {
                        month = dateObj
                            .toLocaleString("en-US", { month: "short" })
                            .toUpperCase();
                        day = dateObj.getDate().toString();
                    }
                }
            } catch (_) { }

            return {
                month,
                day,
                label: item.text.trim(),
                time: item.timings.trim(),
            };
        });
    }, [alertTiming, hasValidData]);


    const scrollRef = useRef(null);

    // Track screen size changes
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width >= 1024) {
                setScreenSize('desktop');
            } else if (width >= 768) {
                setScreenSize('tablet');
            } else {
                setScreenSize('mobile');
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!hasValidData) return;

        const alertBody = scrollRef.current;
        if (!alertBody) return;

        // Only enable dragging for 4 or more cards
        const cardCount = parsedTimings.length;
        if (cardCount < 4) return;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        const handleMouseDown = (e) => {
            isDown = true;
            alertBody.classList.add("dragging");
            alertBody.style.scrollBehavior = "auto";

            startX = e.pageX;
            scrollLeft = alertBody.scrollLeft;

            document.body.style.overflow = "hidden";
            document.body.style.userSelect = "none";

            e.preventDefault();
        };

        const handleMouseMove = (e) => {
            if (!isDown) return;
            const walk = (e.pageX - startX) * 2;
            alertBody.scrollLeft = scrollLeft - walk;
        };

        const handleMouseUp = () => {
            isDown = false;
            alertBody.classList.remove("dragging");
            alertBody.style.scrollBehavior = "smooth";

            document.body.style.overflow = "";
            document.body.style.userSelect = "";
        };

        alertBody.addEventListener("mousedown", handleMouseDown);
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            alertBody.removeEventListener("mousedown", handleMouseDown);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [hasValidData, parsedTimings.length]);

    useEffect(() => {
        if (!hasValidData) return;

        const el = scrollRef.current;
        if (!el) return;

        // Only enable wheel scrolling for 4 or more cards
        const cardCount = parsedTimings.length;
        if (cardCount < 4) return;

        const onWheel = (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                e.stopPropagation();
                el.scrollLeft += e.deltaY;
            }
        };

        el.addEventListener("wheel", onWheel, { passive: false });

        return () => {
            el.removeEventListener("wheel", onWheel);
        };
    }, [hasValidData, parsedTimings.length]);

    // Responsive layout logic based on screen size and card count
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !hasValidData) return;

        const cardCount = parsedTimings.length;
        let shouldCenter = false;

        // Apply responsive justify-content rules
        if (screenSize === 'desktop') {
            // Desktop: 1-3 cards → center, 4+ → flex-start
            shouldCenter = cardCount >= 1 && cardCount <= 3;
        } else if (screenSize === 'tablet') {
            // Tablet: 1-2 cards → center, 3+ → flex-start
            shouldCenter = cardCount >= 1 && cardCount <= 2;
        } else {
            // Mobile: 1 card → center, 2+ → flex-start
            shouldCenter = cardCount === 1;
        }

        // Apply styles
        if (shouldCenter) {
            el.style.justifyContent = "center";
            el.style.overflowX = "hidden";
            el.style.cursor = "default";
            el.scrollLeft = 0;
        } else {
            el.style.justifyContent = "flex-start";
            // Only enable auto overflow for 4+ cards, otherwise visible
            el.style.overflowX = cardCount >= 4 ? "auto" : "visible";
            // Only enable grab cursor for 4+ cards
            el.style.cursor = cardCount >= 4 ? "grab" : "default";
            el.scrollLeft = 0; // Reset scroll to start
        }

        // Manage data-lenis-prevent attribute dynamically
        if (cardCount >= 4) {
            el.setAttribute('data-lenis-prevent', 'true');
        } else {
            el.removeAttribute('data-lenis-prevent');
        }
    }, [parsedTimings.length, screenSize, hasValidData]);



    if (!hasValidData) return null;


    const containerClass = parentClass || "canvas alerts";
    
    return (
        <div className={containerClass}>
            <div className="hours-alerts">
                <div className="hc-alerts" data-stagger-index="2">
                    <div className="hc-alert-headers">
                        <p className="hc-alert-titles">
                            {alertTitle} ({parsedTimings.length})
                        </p>
                    </div>

                    <div
                        className={`hc-alert-bodys ${parsedTimings.length === 4 ? "four-cards" : ""}`}
                        ref={scrollRef}
                    >

                        {parsedTimings.map((item, index) => (
                            <div className="closure-cards" key={index}>
                                <div className="closure-dates">
                                    <span className="months">{item.month}</span>
                                    <span className="days">{item.day}</span>
                                </div>
                                <div className="closure-infos">
                                    <p className="closure-labels">{item.label}</p>
                                    <p className="closure-times">{item.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div >
    );
}
